import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCircle, FaSyncAlt, FaLock, FaBroadcastTower } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import monitoringApi from '../../../services/monitoringApi';
import { getMonitoringSocket } from '../../../services/monitoringSocket';
import { METRICS, RANGES, formatTimestamp, timeAgo } from './monitoringUtils';
import SensorCard from './components/SensorCard';
import TrendChart from './components/TrendChart';
import AlertFeed from './components/AlertFeed';
import HealthReportPanel from './components/HealthReportPanel';

const METRIC_COLORS = {
  temperature: '#dc2626',
  humidity: '#2563eb',
  co2: '#7c3aed',
  ammonia: '#d97706',
};

export default function RealTimeMonitoring() {
  const { user } = useContext(AuthContext);
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [deviceMeta, setDeviceMeta] = useState(null);
  const [latest, setLatest] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [points, setPoints] = useState([]);
  const [range, setRange] = useState('24h');
  const [alerts, setAlerts] = useState([]);
  const [report, setReport] = useState(null);
  const [reportRange, setReportRange] = useState('7d');
  const [reportLoading, setReportLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  // ── Load devices ───────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await monitoringApi.listDevices();
        if (!mounted) return;
        setDevices(res.devices || []);
        if (res.devices?.length) setSelectedId(res.devices[0].deviceId);
      } catch (err) {
        const msg = typeof err === 'string' ? err : err?.error || '';
        if (msg.toLowerCase().includes('subscription')) setAccessDenied(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ── Live socket ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (accessDenied) return undefined;
    const socket = getMonitoringSocket();

    const onReading = ({ deviceId, reading }) => {
      setDevices((prev) =>
        prev.map((d) =>
          d.deviceId === deviceId
            ? { ...d, latestReading: reading, status: 'online', lastSeenAt: reading.timestamp, flockAgeDays: reading.flockAgeDays }
            : d
        )
      );
      if (deviceId === selectedIdRef.current) {
        setLatest(reading);
        setDeviceMeta((m) => (m ? { ...m, status: 'online', lastSeenAt: reading.timestamp, flockAgeDays: reading.flockAgeDays } : m));
        setPoints((prev) => {
          const next = [...prev, pointFromReading(reading)];
          return next.slice(-250);
        });
      }
    };

    const onAlert = ({ deviceId, alert }) => {
      if (deviceId !== selectedIdRef.current) {
        // Keep device-level active counts roughly fresh.
        setDevices((prev) => prev.map((d) => (d.deviceId === deviceId ? { ...d } : d)));
        return;
      }
      setAlerts((prev) => {
        const without = prev.filter((a) => a._id !== alert._id);
        if (alert.status === 'resolved') return without;
        return [alert, ...without];
      });
      if (alert.status === 'active' && alert.level === 'critical') {
        toast.warn(`Critical: ${alert.message}`, { toastId: alert._id });
      }
    };

    const onStatus = ({ deviceId, status, lastSeenAt }) => {
      setDevices((prev) => prev.map((d) => (d.deviceId === deviceId ? { ...d, status, lastSeenAt } : d)));
      if (deviceId === selectedIdRef.current) {
        setDeviceMeta((m) => (m ? { ...m, status, lastSeenAt } : m));
      }
    };

    socket.on('reading', onReading);
    socket.on('alert', onAlert);
    socket.on('deviceStatus', onStatus);

    return () => {
      socket.off('reading', onReading);
      socket.off('alert', onAlert);
      socket.off('deviceStatus', onStatus);
    };
  }, [accessDenied]);

  // ── Subscribe to the selected device room + load its detail ──────────────────
  useEffect(() => {
    if (!selectedId) return undefined;
    const socket = getMonitoringSocket();
    socket.emit('subscribeDevice', selectedId);

    let mounted = true;
    (async () => {
      try {
        const [latestRes, alertRes, reportRes] = await Promise.all([
          monitoringApi.getLatest(selectedId),
          monitoringApi.getAlerts({ deviceId: selectedId, status: 'active' }),
          monitoringApi.listHealthReports(selectedId),
        ]);
        if (!mounted) return;
        setDeviceMeta(latestRes.device);
        setLatest(latestRes.latestReading);
        setThresholds(latestRes.thresholds);
        setAlerts(alertRes.alerts || []);
        setReport(reportRes.reports?.[0] || null);
      } catch (err) {
        /* handled by interceptor */
      }
    })();

    return () => {
      mounted = false;
      socket.emit('unsubscribeDevice', selectedId);
    };
  }, [selectedId]);

  // ── Load history for the selected device + range ─────────────────────────────
  useEffect(() => {
    if (!selectedId) return undefined;
    let mounted = true;
    setHistoryLoading(true);
    (async () => {
      try {
        const res = await monitoringApi.getHistory(selectedId, range);
        if (mounted) setPoints(res.points || []);
      } catch (err) {
        if (mounted) setPoints([]);
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedId, range]);

  const handleResolve = async (alert) => {
    try {
      await monitoringApi.resolveAlert(alert._id);
      setAlerts((prev) => prev.filter((a) => a._id !== alert._id));
      toast.success('Alert resolved');
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const res = await monitoringApi.generateHealthReport(selectedId, reportRange);
      setReport(res.report);
      toast.success('AI health analysis generated');
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.error || 'Failed to generate analysis';
      toast.error(msg);
    } finally {
      setReportLoading(false);
    }
  };

  const selectedDevice = useMemo(
    () => devices.find((d) => d.deviceId === selectedId),
    [devices, selectedId]
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading monitoring…</div>;
  }

  if (accessDenied) {
    return <SubscriptionGate />;
  }

  if (!devices.length) {
    return (
      <div className="max-w-xl mx-auto mt-10 bg-white border border-gray-200 rounded-xl p-8 text-center">
        <FaBroadcastTower className="mx-auto text-3xl text-primary mb-3" />
        <h2 className="text-lg font-semibold text-gray-800">No monitored houses yet</h2>
        <p className="text-sm text-gray-500 mt-2">
          Once your poultry house sensors are registered and reporting, their live climate data will
          appear here.
        </p>
      </div>
    );
  }

  const online = deviceMeta?.status === 'online';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Real-Time Monitoring</h1>
          <p className="text-sm text-gray-500">Live poultry house climate &amp; AI health insights</p>
        </div>
        <select
          value={selectedId || ''}
          onChange={(e) => setSelectedId(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white"
        >
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.houseName || d.name} · {d.deviceId}
              {d.farmer ? ` · ${d.farmer.companyName || `${d.farmer.firstName || ''} ${d.farmer.lastName || ''}`.trim() || d.farmer.email}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Device status strip */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-x-8 gap-y-3 shadow-sm">
        <StatusItem
          label="Device"
          value={
            <span className={`inline-flex items-center gap-2 font-semibold ${online ? 'text-green-600' : 'text-gray-400'}`}>
              <FaCircle className="text-[8px]" /> {online ? 'Online' : 'Offline'}
            </span>
          }
        />
        <StatusItem label="House" value={deviceMeta?.houseName || selectedDevice?.name || '—'} />
        <StatusItem label="Flock age" value={`${deviceMeta?.flockAgeDays ?? '—'} days`} />
        <StatusItem label="Capacity" value={selectedDevice?.capacity ? `${selectedDevice.capacity.toLocaleString()} birds` : '—'} />
        <StatusItem
          label="Last updated"
          value={latest ? `${formatTimestamp(latest.timestamp)} (${timeAgo(latest.timestamp)})` : '—'}
        />
      </div>

      {/* Live sensor cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {METRICS.map((m) => (
          <SensorCard
            key={m.key}
            metric={m.key}
            value={latest?.[m.key]}
            status={latest?.statuses?.[m.key] || 'optimal'}
            decimals={m.decimals}
            unit={m.unit}
            optimalRange={thresholds?.[m.key]?.optimal}
          />
        ))}
      </div>

      {/* Trend charts + alerts/AI */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Historical Trends</h2>
            <div className="flex items-center gap-2">
              {historyLoading && <FaSyncAlt className="animate-spin text-gray-400 text-sm" />}
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    className={`px-3 py-1.5 text-xs font-medium ${
                      range === r.key ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {METRICS.map((m) => (
              <TrendChart
                key={m.key}
                title={m.label}
                unit={m.unit}
                metric={m.key}
                points={points}
                color={METRIC_COLORS[m.key]}
              />
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Active Alerts</h2>
            <AlertFeed alerts={alerts} onResolve={handleResolve} emptyText="All conditions optimal." />
          </div>
          <HealthReportPanel
            report={report}
            loading={reportLoading}
            onGenerate={handleGenerateReport}
            range={reportRange}
            onRangeChange={setReportRange}
          />
        </div>
      </div>
    </div>
  );
}

function StatusItem({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 mt-0.5">{value}</div>
    </div>
  );
}

function SubscriptionGate() {
  return (
    <div className="max-w-xl mx-auto mt-10 bg-white border border-gray-200 rounded-xl p-8 text-center">
      <span className="mx-auto w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl mb-3">
        <FaLock />
      </span>
      <h2 className="text-lg font-semibold text-gray-800">Subscription required</h2>
      <p className="text-sm text-gray-500 mt-2">
        Real-Time Monitoring is available to farmers with an active subscription.
      </p>
      <Link
        to="/farmer/subscription"
        className="inline-block mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
      >
        View subscription plans
      </Link>
    </div>
  );
}

function pointFromReading(reading) {
  return {
    timestamp: reading.timestamp,
    temperature: reading.temperature,
    humidity: reading.humidity,
    co2: reading.co2,
    ammonia: reading.ammonia,
  };
}
